import React, { useState, useEffect } from 'react';

/**
 * Floating WhatsApp click-to-chat button.
 * Shows on every public page, fixed bottom-right.
 * Mobile (<sm): shows a compact icon-only pill.
 * Desktop: icon + small "Chat with us" label that appears on hover.
 *
 * Number: +91 9419016969 (digits only for wa.me)
 */
const WHATSAPP_NUMBER = '919419016969';
const PREFILLED_MESSAGE =
  "Hi Velmora Holidays! I'm interested in planning a Kashmir trip. Could you help me with a custom tour package?";

export const WhatsAppButton = () => {
  const [visible, setVisible] = useState(false);

  // Fade-in after a short delay so it doesn't fight with hero entrance animations
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 800);
    return () => clearTimeout(t);
  }, []);

  const href = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
    PREFILLED_MESSAGE
  )}`;

  // Fire a GA4 event (and conversion) on every click for lead-inquiry attribution.
  // Safe no-op if gtag is not loaded (e.g. AdBlock or GA script not yet ready).
  const handleClick = () => {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', 'whatsapp_click', {
        event_category: 'engagement',
        event_label: 'floating_button',
        transport_type: 'beacon',
        value: 1,
      });
      // Mark as a conversion using GA4 recommended parameter
      window.gtag('event', 'generate_lead', {
        event_category: 'lead',
        event_label: 'whatsapp',
        method: 'whatsapp',
        value: 1,
      });
    }
  };

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleClick}
      aria-label="Chat with Velmora Holidays on WhatsApp"
      data-testid="whatsapp-float-btn"
      className={`group fixed bottom-5 right-5 sm:bottom-6 sm:right-6 z-50 flex items-center gap-2 rounded-full bg-[#128C7E] px-4 py-3 text-white shadow-lg shadow-green-800/30 ring-1 ring-black/10 transition-all duration-500 hover:bg-[#0d6e64] hover:shadow-xl hover:scale-105 min-h-[48px] min-w-[48px] ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
      }`}
    >
      {/* Pulse ring */}
      <span
        aria-hidden="true"
        className="absolute inset-0 rounded-full bg-[#128C7E] opacity-60 animate-ping"
        style={{ animationDuration: '2.5s' }}
      />

      {/* WhatsApp icon (inline SVG to avoid extra deps) */}
      <svg
        viewBox="0 0 32 32"
        className="relative z-10 h-6 w-6 fill-current"
        aria-hidden="true"
      >
        <path d="M19.11 17.29c-.27-.14-1.62-.8-1.87-.89-.25-.09-.43-.14-.62.14-.18.27-.71.89-.87 1.07-.16.18-.32.21-.59.07-.27-.14-1.15-.42-2.19-1.34-.81-.72-1.36-1.61-1.52-1.88-.16-.27-.02-.42.12-.55.12-.12.27-.32.41-.48.14-.16.18-.27.27-.45.09-.18.05-.34-.02-.48-.07-.14-.62-1.49-.85-2.04-.22-.54-.45-.46-.62-.47-.16-.01-.34-.01-.52-.01-.18 0-.48.07-.73.34-.25.27-.96.94-.96 2.29 0 1.35.99 2.65 1.13 2.83.14.18 1.95 2.98 4.74 4.18.66.29 1.18.46 1.58.59.66.21 1.27.18 1.74.11.53-.08 1.62-.66 1.85-1.3.23-.64.23-1.18.16-1.3-.07-.12-.25-.18-.52-.32zM16.02 4.79c-6.18 0-11.21 5.03-11.21 11.21 0 1.97.51 3.9 1.49 5.59L4.8 27.2l5.78-1.51c1.63.89 3.46 1.36 5.31 1.36h.01c6.18 0 11.21-5.03 11.21-11.21S22.2 4.79 16.02 4.79zm0 20.55c-1.65 0-3.27-.44-4.7-1.27l-.34-.2-3.5.92.94-3.41-.22-.35a9.286 9.286 0 0 1-1.43-4.97c0-5.13 4.18-9.31 9.31-9.31 2.49 0 4.82.97 6.58 2.73a9.244 9.244 0 0 1 2.73 6.58c0 5.13-4.18 9.31-9.37 9.31z" />
      </svg>

      <span className="relative z-10 text-sm font-semibold whitespace-nowrap">
        Chat on WhatsApp
      </span>
    </a>
  );
};

export default WhatsAppButton;
